"use client"

import { useState, useEffect } from "react"
import { usePathname } from "next/navigation"
import { Bell, X, Loader2 } from "lucide-react"

const DISMISSED_KEY = "push_prompt_dismissed"

// Only prompt on pages where the user has seen real value
const VALUE_PAGES = ["/feed", "/projects", "/messages", "/jobs", "/connections", "/network"]

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/")
  const rawData = window.atob(base64)
  const bytes = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; i++) bytes[i] = rawData.charCodeAt(i)
  return bytes
}

export default function NotificationPrompt() {
  const [visible, setVisible] = useState(false)
  const [loading, setLoading] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    if (typeof window === "undefined") return
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return
    if (Notification.permission !== "default") return
    if (localStorage.getItem(DISMISSED_KEY)) return

    // Only prompt when the user is on a page where they've seen value
    const isValuePage = VALUE_PAGES.some((p) => pathname.startsWith(p))
    if (!isValuePage) return

    // Wait 30s on a value page before prompting — they've had time to engage
    const t = setTimeout(() => setVisible(true), 30000)
    return () => clearTimeout(t)
  }, [pathname])

  function dismiss() {
    localStorage.setItem(DISMISSED_KEY, "1")
    setVisible(false)
  }

  async function handleEnable() {
    setLoading(true)
    try {
      const perm = await Notification.requestPermission()
      if (perm !== "granted") { dismiss(); return }

      const reg = await navigator.serviceWorker.register("/sw.js")
      await navigator.serviceWorker.ready

      const keyRes = await fetch("/api/push/subscribe")
      const { publicKey } = await keyRes.json()
      if (!publicKey) { dismiss(); return }

      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })

      await fetch("/api/push/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(sub.toJSON()),
      })

      dismiss()
    } catch (err) {
      console.error("Push subscribe error", err)
      dismiss()
    }
  }

  if (!visible) return null

  return (
    <div className="fixed bottom-[calc(56px+env(safe-area-inset-bottom))] md:bottom-4 left-0 right-0 md:left-auto md:right-4 md:max-w-sm z-40 px-3 md:px-0">
      <div className="bg-gray-900 text-white rounded-xl shadow-xl p-4 flex items-start gap-3">
        <div className="flex-shrink-0 bg-blue-500 rounded-full p-2 mt-0.5">
          <Bell className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">Stay on top of your projects</p>
          <p className="text-xs text-gray-400 mt-0.5">
            Get notified when you receive messages, bids, or scope updates.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleEnable}
              disabled={loading}
              className="flex items-center gap-1.5 bg-blue-500 hover:bg-blue-600 disabled:opacity-60 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
            >
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bell className="h-3.5 w-3.5" />}
              Enable notifications
            </button>
            <button
              onClick={dismiss}
              className="text-gray-400 hover:text-white text-xs px-2 py-1.5 transition-colors"
            >
              Not now
            </button>
          </div>
        </div>
        <button onClick={dismiss} className="flex-shrink-0 text-gray-500 hover:text-white transition-colors">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
